import React from "react";
import { AVATAR_SECTION_PATH, AVATAR_SIGNUP_PATH, fetchFaceLockCrew, fetchFaceLockOpen } from "@/lib/facelock";

// The site's way in to avatars (2026-10-02, owner: "there is no create avatar
// button anywhere"): the navbar entry (bar and phone menu) and the landing
// page's "Create your avatar" link, both to the avatar section on the account
// page (/chopshop#casting-crew), where Create avatar is at the top.
//
// Shown only where the server says avatars are on, never faked:
//   signed in   GET /avatars answers (404 feature_off -> no entry);
//   signed out  GET /open says avatars are open to every account; the entry
//               then goes to sign up (log in from there) and back.
// One answer per session state, kept for a minute, so every page's navbar
// does not ask again.

export type AvatarEntry = { show: boolean; href: string };

const TTL_MS = 60_000;
let cached: { key: string; at: number; show: boolean } | null = null;
let inflight: { key: string; promise: Promise<boolean> } | null = null;

const tokenNow = (): string => {
  try { return localStorage.getItem("auth_token") || ""; } catch { return ""; }
};

/** Tests: forget the kept answer. */
export function resetAvatarEntryCache(): void {
  cached = null;
  inflight = null;
}

function load(signedIn: boolean): Promise<boolean> {
  const key = signedIn ? `in:${tokenNow()}` : "out";
  if (cached && cached.key === key && Date.now() - cached.at < TTL_MS) return Promise.resolve(cached.show);
  if (inflight && inflight.key === key) return inflight.promise;
  const promise = (signedIn
    ? fetchFaceLockCrew().then((r) => r.state === "on")
    : fetchFaceLockOpen()
  ).then((show) => {
    cached = { key, at: Date.now(), show };
    if (inflight && inflight.key === key) inflight = null;
    return show;
  }, () => {
    if (inflight && inflight.key === key) inflight = null;
    return false;
  });
  inflight = { key, promise };
  return promise;
}

/** `signedIn`: the navbar passes useAuth's answer; elsewhere a saved session token counts. */
export function useAvatarEntry(signedIn?: boolean): AvatarEntry {
  const inNow = signedIn ?? !!tokenNow();
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    void load(inNow).then((s) => { if (alive) setShow(s); });
    return () => { alive = false; };
  }, [inNow]);
  return { show, href: inNow ? AVATAR_SECTION_PATH : AVATAR_SIGNUP_PATH };
}
